import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type {
  AdditionalRecipient,
  EncompassRoleOption
} from '@/services/notificationSettingsService';
import type { RecipientTargetKind } from '../../../../supabase/functions/_shared/notification-recipient-contract';
import { isSameTarget, type RecipientTarget } from './types';
import { LOAN_CONTACT_LABELS, LOAN_PARTY_LABELS, LOAN_ROLE_LABELS } from './config';

interface RecipientPickerProps {
  availableUsers: AdditionalRecipient[];
  userRoles: string[];
  encompassRoles: EncompassRoleOption[];
  allowedKinds: readonly RecipientTargetKind[];
  unavailableTargets?: RecipientTarget[];
  disabled?: boolean;
  onAdd: (target: RecipientTarget) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PickerRoleOption {
  key: string;
  label: string;
  detail: string;
  color: string;
  target: RecipientTarget;
  group: 'Portal roles' | 'Encompass loan roles' | 'Loan parties' | 'Loan contacts';
  legacySlug?: string | null;
}

function includesQuery(...values: Array<string | null | undefined>) {
  return (query: string) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return true;
    }
    return values.some((value) => value?.toLowerCase().includes(normalized));
  };
}

export function RecipientPicker({
  availableUsers,
  userRoles,
  encompassRoles,
  allowedKinds,
  unavailableTargets = [],
  disabled,
  onAdd
}: RecipientPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const allowed = new Set<RecipientTargetKind>(allowedKinds);
  const showUsers = allowed.has('user');
  const showEmailInput = allowed.has('raw_email');
  const showPortalRoles = allowed.has('user_role');
  const showEncompassRoles = allowed.has('encompass_loan_role');
  const showLegacyRoles = allowed.has('loan_role') && !showEncompassRoles;
  const showLoanParties = allowed.has('loan_party');
  const showLoanContacts = allowed.has('loan_contact');
  const hasPeopleOptions = showUsers || showEmailInput;
  const triggerLabel = hasPeopleOptions ? 'Add recipient' : 'Add role';
  const inputPlaceholder = showEmailInput
    ? 'Search people, roles, or type an email...'
    : showUsers
      ? 'Search people or roles...'
      : 'Search roles...';
  const queryMatches = includesQuery;

  const isUnavailable = (
    target: RecipientTarget,
    legacySlug?: string | null
  ) => unavailableTargets.some((unavailableTarget) => {
    if (isSameTarget(unavailableTarget, target)) {
      return true;
    }
    return target.kind === 'encompass_loan_role'
      && unavailableTarget.kind === 'loan_role'
      && Boolean(legacySlug)
      && unavailableTarget.role === legacySlug;
  });

  const roleOptions: PickerRoleOption[] = [
    ...(showPortalRoles
      ? userRoles.map((role) => ({
          key: `portal:${role}`,
          label: role,
          detail: 'Portal role',
          color: '#1d4ed8',
          group: 'Portal roles' as const,
          target: { kind: 'user_role' as const, role }
        }))
      : []),
    ...(showEncompassRoles
      ? encompassRoles.map((role) => ({
          key: `encompass:${role.role_id}`,
          label: role.role_name,
          detail: `Encompass role ${role.role_id}`,
          color: '#0f766e',
          group: 'Encompass loan roles' as const,
          legacySlug: role.legacy_slug,
          target: {
            kind: 'encompass_loan_role' as const,
            role_id: role.role_id,
            role_name_snapshot: role.role_name
          }
        }))
      : []),
    ...(showLegacyRoles
      ? (Object.entries(LOAN_ROLE_LABELS) as Array<[
          Extract<RecipientTarget, { kind: 'loan_role' }>['role'],
          string
        ]>).map(([role, label]) => ({
          key: `legacy:${role}`,
          label,
          detail: role,
          color: '#2563eb',
          group: 'Encompass loan roles' as const,
          target: { kind: 'loan_role' as const, role }
        }))
      : []),
    ...(showLoanParties
      ? (Object.entries(LOAN_PARTY_LABELS) as Array<[
          Extract<RecipientTarget, { kind: 'loan_party' }>['party'],
          string
        ]>).map(([party, label]) => ({
          key: `party:${party}`,
          label,
          detail: party,
          color: '#7c3aed',
          group: 'Loan parties' as const,
          target: { kind: 'loan_party' as const, party }
        }))
      : []),
    ...(showLoanContacts
      ? (Object.entries(LOAN_CONTACT_LABELS) as Array<[
          Extract<RecipientTarget, { kind: 'loan_contact' }>['contact'],
          string
        ]>).map(([contact, label]) => ({
          key: `contact:${contact}`,
          label,
          detail: contact,
          color: '#c2410c',
          group: 'Loan contacts' as const,
          target: { kind: 'loan_contact' as const, contact }
        }))
      : [])
  ];

  const filteredUsers = showUsers
    ? availableUsers.filter((user) => (
        !isUnavailable({ kind: 'user', user_id: user.id })
        && queryMatches(user.full_name, user.email)(query)
      ))
    : [];
  const filteredRoles = roleOptions.filter((option) => (
    !isUnavailable(option.target, option.legacySlug)
    && queryMatches(option.label, option.detail, option.group)(query)
  ));
  const visibleRoleGroups = Array.from(
    new Set(filteredRoles.map((option) => option.group))
  );
  const hasVisibleOptions = filteredUsers.length > 0 || filteredRoles.length > 0;
  const normalizedQuery = query.trim().toLowerCase();
  const canAddTypedEmail = showEmailInput
    && EMAIL_RE.test(normalizedQuery)
    && !isUnavailable({ kind: 'raw_email', email: normalizedQuery })
    && !availableUsers.some((user) => user.email.toLowerCase() === normalizedQuery);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery('');
    }
  };

  const addTarget = (target: RecipientTarget) => {
    onAdd(target);
    setQuery('');
    setOpen(false);
  };

  const addTypedEmail = () => {
    if (!canAddTypedEmail) {
      return;
    }
    addTarget({ kind: 'raw_email', email: normalizedQuery });
  };

  const initialsFor = (user: AdditionalRecipient) => {
    const source = user.full_name || user.email;
    const initials = source
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('');
    return initials.toUpperCase() || user.email.slice(0, 2).toUpperCase();
  };

  const titleFor = (user: AdditionalRecipient) => {
    const role = Array.isArray(user.roles) ? user.roles[0] : null;
    if (!role) {
      return 'Internal user';
    }
    return role
      .split(/[_-]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-preserve-disabled className="en-zone-add">
          <Plus className="h-3.5 w-3.5" />
          {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={hasPeopleOptions
          ? 'en-recipient-picker en-recipient-picker-people'
          : 'en-recipient-picker'}
        align="end"
        sideOffset={4}
      >
        <div className="en-recipient-picker-search">
          <input
            autoFocus
            aria-label="Search recipient options"
            className="en-recipient-picker-input"
            placeholder={inputPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && canAddTypedEmail) {
                addTypedEmail();
              }
              if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
        </div>
        <div className="en-recipient-picker-list">
          {filteredUsers.length > 0 && (
            <>
              <div className="en-recipient-picker-heading">People</div>
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className="en-recipient-picker-user-row"
                  onClick={() => addTarget({ kind: 'user', user_id: user.id })}
                >
                  <span className="en-recipient-picker-avatar">{initialsFor(user)}</span>
                  <span className="en-recipient-picker-user-copy">
                    <span className="en-recipient-picker-user-name">{user.full_name}</span>
                    <span className="en-recipient-picker-user-email">
                      {titleFor(user)} · {user.email}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}
          {visibleRoleGroups.map((group) => (
            <React.Fragment key={group}>
              <div className="en-recipient-picker-heading">{group}</div>
              {filteredRoles
                .filter((option) => option.group === group)
                .map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className="en-recipient-picker-role-row"
                    onClick={() => addTarget(option.target)}
                  >
                    <span
                      className="en-recipient-picker-role-dot"
                      style={{ backgroundColor: option.color }}
                    />
                    <span className="en-recipient-picker-role-label">{option.label}</span>
                    <span className="en-recipient-picker-role-key">{option.detail}</span>
                  </button>
                ))}
            </React.Fragment>
          ))}
          {!hasVisibleOptions && (
            <div className="en-recipient-picker-empty">No matches</div>
          )}
          {canAddTypedEmail && (
            <>
              <div className="en-recipient-picker-separator" />
              <button
                type="button"
                className="en-recipient-picker-add-row"
                data-preserve-disabled
                onClick={addTypedEmail}
              >
                <Plus className="h-3 w-3" />
                Add &quot;{query.trim()}&quot;
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
